import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  ReactFlow,
  Background,
  useNodesState,
  useEdgesState,
  MarkerType,
  ReactFlowProvider,
  Controls,
  MiniMap,
  BackgroundVariant,
  useReactFlow,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import KnowledgeGraphNode, { findNodeColor, findNodeLabel } from "./KnowledgeGraphNode";
import { ColumnType, MetadataTypeEnum, MetricEdgeType } from "build-ts/enums";
import { Header, MetricProto } from "build-ts/metadata";
import {
  forceSimulation,
  forceLink,
  forceManyBody,
  forceCenter,
  forceCollide,
} from "d3-force";
import { useConfigurationContext } from "context/ConfigurationContext";
import {
  Box,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Tooltip,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import { FiMap } from "react-icons/fi";
import GraphLegend, { LegendKind } from "./GraphLegend";
import MKGNodePopupContainer from "containers/configuration/mkg/MKGNodePopupContainer";

export type KnowledgeGraphVariant = "full" | "subgraph";

interface KnowledgeGraphVisualizerProps {
  metricInfoMap: Map<string, MetricProto>;
  /** `subgraph`: single-metric panel — tighter layout, no search, legend starts collapsed */
  variant?: KnowledgeGraphVariant;
}

const nodeTypes = {
  customMKGNode: KnowledgeGraphNode,
};

// Derived edge color, consistent between sidebar-select and node-click paths
function resolveEdgeColor(edge: any, metricNodesData: any[]): string {
  if (edge.source && edge.target) {
    const targetNode = metricNodesData.find((n: any) => n.id === edge.target);
    if (targetNode?.data?.header?.type !== undefined) {
      const c = findNodeColor(targetNode.data.header);
      if (c) return c;
    }
  }
  return "#8133b2";
}

function applyHighlight(
  nodeId: string,
  originalEdges: any[],
  metricNodesData: any[]
): { nodes: any[]; edges: any[] } {
  const connectedEdgeIds = new Set(
    originalEdges
      .filter(e => e.source === nodeId || e.target === nodeId)
      .map(e => e.id)
  );
  const connectedNodeIds = new Set([
    nodeId,
    ...originalEdges
      .filter(e => e.source === nodeId || e.target === nodeId)
      .map(e => (e.source === nodeId ? e.target : e.source)),
  ]);

  const edges = originalEdges.map(e => {
    const isConnected = connectedEdgeIds.has(e.id);
    const color = resolveEdgeColor(e, metricNodesData);
    return {
      ...e,
      style: {
        ...e.style,
        stroke: isConnected ? color : "rgba(200,200,200,0.25)",
        strokeWidth: isConnected ? 3 : 2,
      },
      markerEnd: isConnected
        ? { type: MarkerType.ArrowClosed, color, width: 10, height: 10 }
        : undefined,
    };
  });

  const nodes = metricNodesData.map(n => ({
    ...n,
    data: {
      ...n.data,
      searchDimmed: false,
      searchHighlighted: false,
    },
    style: {
      ...n.style,
      opacity: connectedNodeIds.has(n.id) ? 1 : 0.25,
    },
  }));

  return { nodes, edges };
}

function resetGraph(
  originalEdges: any[],
  metricNodesData: any[]
): { nodes: any[]; edges: any[] } {
  const edges = originalEdges.map(e => ({
    ...e,
    style: { ...e.style, stroke: "#e2e8f0", strokeWidth: 2 },
    markerEnd: undefined,
  }));
  const nodes = metricNodesData.map(n => ({
    ...n,
    data: { ...n.data, searchDimmed: false, searchHighlighted: false },
    style: { ...n.style, opacity: 1 },
  }));
  return { nodes, edges };
}

function getLegendKind(header: Header | undefined): LegendKind | null {
  if (!header?.type) return null;
  if (header.type === MetadataTypeEnum.METRIC) return "metric";
  if (header.type === MetadataTypeEnum.BENCHMARK) return "benchmark";
  if (header.type === MetadataTypeEnum.KG_COLUMN && header.column_type === ColumnType.ATTRIBUTE) return "attribute";
  if (header.type === MetadataTypeEnum.KG_COLUMN && header.column_type === ColumnType.MEASURE) return "measure";
  if (header.type === MetadataTypeEnum.KG_COLUMN && header.column_type === ColumnType.TEMPORAL) return "time";
  return null;
}

function applyLegendTypeFilter(
  kinds: Set<LegendKind>,
  originalEdges: any[],
  metricNodesData: any[]
): { nodes: any[]; edges: any[] } {
  const nodeById = new Map(metricNodesData.map((n: any) => [n.id, n]));

  const nodes = metricNodesData.map((n: any) => {
    const lk = getLegendKind(n.data?.header);
    const match = lk !== null && kinds.has(lk);
    return {
      ...n,
      data: {
        ...n.data,
        searchDimmed: !match,
        searchHighlighted: false,
      },
      style: {
        ...n.style,
        opacity: match ? 1 : 0.25,
      },
    };
  });

  const edges = originalEdges.map(e => {
    const sn = nodeById.get(e.source)?.data?.header;
    const tn = nodeById.get(e.target)?.data?.header;
    const sk = sn ? getLegendKind(sn) : null;
    const tk = tn ? getLegendKind(tn) : null;
    const sm = sk !== null && kinds.has(sk);
    const tm = tk !== null && kinds.has(tk);
    const bright = sm && tm;
    const color = resolveEdgeColor(e, metricNodesData);
    return {
      ...e,
      style: {
        ...e.style,
        stroke: bright ? color : "rgba(200,200,200,0.22)",
        strokeWidth: bright ? 3 : 2,
      },
      markerEnd: bright
        ? { type: MarkerType.ArrowClosed, color, width: 10, height: 10 }
        : undefined,
    };
  });

  return { nodes, edges };
}

// ─── Main visualizer ─────────────────────────────────────────────────────────
const KnowledgeGraphVisualizerFlow: React.FC<KnowledgeGraphVisualizerProps> = ({
  metricInfoMap,
  variant = "full",
}) => {
  const isSubgraph = variant === "subgraph";
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [originalEdges, setOriginalEdges] = useState<any[]>([]);
  const [layoutReady, setLayoutReady] = useState(false);
  const {
    selectedMetric, setSelectedMetric,
    selectedBenchmark, setSelectedBenchmark,
    selectedKgColumn, setSelectedKgColumn,
    fetchBenchmarkById,
  } = useConfigurationContext();
  const [metricNodesData, setMetricNodesData] = useState<any[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState({ width: 1200, height: 800 });
  const [searchQuery, setSearchQuery] = useState("");
  const [showMinimap, setShowMinimap] = useState(false);
  const [showLegend, setShowLegend] = useState(!isSubgraph);
  const [legendFilterKinds, setLegendFilterKinds] = useState<LegendKind[]>([]);
  const { fitView } = useReactFlow();

  /** Full graph: hover shows subgraph highlight without changing sidebar / selection */
  const [hoverNodeId, setHoverNodeId] = useState<string | null>(null);

  // Floating panel state
  const [clickPos, setClickPos] = useState<{ x: number; y: number } | null>(null);
  const [panelHeader, setPanelHeader] = useState<Header | null>(null);
  /** Full graph: after closing the edit panel, next pane click clears selection; first pane click only closes panel */
  const paneAwaitingSecondClickToClearRef = useRef(false);
  /** Tracks whether the floating panel is currently open; */
  const panelOpenRef = useRef(false);
  /** Cancellable timer for clearing hoverNodeId to prevent fake mouseleave flicker when setNodes re-renders nodes */
  const hoverLeaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const toggleLegendKind = useCallback((kind: LegendKind) => {
    setLegendFilterKinds((prev) =>
      prev.includes(kind) ? prev.filter((k) => k !== kind) : [...prev, kind]
    );
  }, []);

  // Keep panelOpenRef in sync so hover callbacks can read it without causing re-creation
  useEffect(() => {
    panelOpenRef.current = clickPos !== null && panelHeader !== null;
  }, [clickPos, panelHeader]);

  // ─── Search + sidebar selection + legend type filter → graph visuals ───
  useEffect(() => {
    if (metricNodesData.length === 0) return;

    const q = searchQuery.trim().toLowerCase();
    if (q) {
      const matchedIds = new Set(
        metricNodesData
          .filter((n: any) => {
            const name: string = n.data?.header?.name?.toLowerCase() || "";
            const typeLabel = findNodeLabel(n.data?.header).toLowerCase();
            return name.includes(q) || typeLabel.includes(q);
          })
          .map((n: any) => n.id)
      );

      const updatedNodes = metricNodesData.map((n: any) => ({
        ...n,
        data: {
          ...n.data,
          searchHighlighted: matchedIds.has(n.id),
          searchDimmed: !matchedIds.has(n.id),
        },
        style: { ...n.style, opacity: 1 },
      }));
      setNodes(updatedNodes as any);

      const { edges: e } = resetGraph(originalEdges, metricNodesData);
      setEdges(e as any);
      return;
    }

    const panelOpen = clickPos !== null && panelHeader !== null;
    if (!isSubgraph && hoverNodeId && !panelOpen) {
      const { nodes: n, edges: e } = applyHighlight(hoverNodeId, originalEdges, metricNodesData);
      setNodes(n as any);
      setEdges(e as any);
      return;
    }

    const nodeData = selectedMetric || selectedBenchmark || selectedKgColumn;
    const nodeId = selectedMetric?.id || selectedBenchmark?.id || selectedKgColumn?.id;

    if (nodeData && nodeId) {
      const { nodes: n, edges: e } = applyHighlight(nodeId, originalEdges, metricNodesData);
      setNodes(n as any);
      setEdges(e as any);
      return;
    }

    if (legendFilterKinds.length > 0) {
      const { nodes: n, edges: e } = applyLegendTypeFilter(
        new Set(legendFilterKinds),
        originalEdges,
        metricNodesData
      );
      setNodes(n as any);
      setEdges(e as any);
    } else {
      const { nodes: n, edges: e } = resetGraph(originalEdges, metricNodesData);
      setNodes(n as any);
      setEdges(e as any);
    }
  }, [
    searchQuery,
    selectedMetric,
    selectedBenchmark,
    selectedKgColumn,
    legendFilterKinds,
    metricNodesData,
    originalEdges,
    hoverNodeId,
    isSubgraph,
    clickPos,
    panelHeader,
  ]);

  // ─── Container size (subgraph: follow panel resize; full: one-shot measure) ─
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    if (isSubgraph) {
      const ro = new ResizeObserver((entries) => {
        const cr = entries[0]?.contentRect;
        if (cr && cr.width > 4 && cr.height > 4) {
          setContainerSize({ width: cr.width, height: cr.height });
        }
      });
      ro.observe(el);
      return () => ro.disconnect();
    }
    const { width, height } = el.getBoundingClientRect();
    if (width > 4 && height > 4) setContainerSize({ width, height });
  }, [isSubgraph]);

  // ─── Build graph from metricInfoMap ──────────────────────────────────────
  useEffect(() => {
    if (!metricInfoMap || metricInfoMap.size === 0) return;
    if (!containerSize.width || !containerSize.height) return;

    const metricNodesData: any[] = [];
    const metricEdgesData: any[] = [];
    const attributeMap = new Map<string, string>();

    const addUniqueNode = (node: any) => {
      if (!metricNodesData.find((n: any) => n.id === node.id)) {
        metricNodesData.push(node);
      }
    };

    // First pass: metric nodes
    metricInfoMap.forEach((metric, metricId) => {
      addUniqueNode({
        id: metricId,
        type: "customMKGNode",
        data: { header: metric?.header },
        position: { x: 0, y: 0 },
      });
    });

    // Second pass: edges + attribute/benchmark/measure/time nodes
    metricInfoMap.forEach((metric, metricId) => {
      metric?.edges?.forEach(edge => {
        switch (edge.edge_type) {
          case MetricEdgeType.METRIC_METRIC_EDGE: {
            if (!edge?.header?.id) break;
            const relId = edge.header.id;
            // Check if the target metric node already exists
            const targetMetricExists = metricNodesData.some(node => node.id === relId);

            // If the metric doesn't exist yet, create a placeholder node
            if (!targetMetricExists) {
              addUniqueNode({
                id: relId,
                type: "customMKGNode",
                data: { header: edge.header },
                position: { x: 0, y: 0 },
              });
            }
            // Create edge between the two metric nodes
            metricEdgesData.push({
              id: `${metricId}-${relId}`,
              source: metricId,
              target: relId,
              animated: false,
              type: "smoothstep",
              style: { stroke: "#e2e8f0", strokeWidth: 2 },
              markerEnd: undefined,
            });
            break;
          }
          case MetricEdgeType.METRIC_ATTRIBUTE_EDGE:
          case MetricEdgeType.METRIC_BENCHMARK_EDGE:
          case MetricEdgeType.METRIC_MEASURE_EDGE:
          case MetricEdgeType.METRIC_TIMEFRAME_EDGE: {
            if (!edge?.header?.name) break;
            const attrName = edge.header.name;
            let attrNodeId = attributeMap.get(attrName);

            // If the attribute node doesn't exist yet, create a placeholder node
            if (!attrNodeId) {
              attrNodeId = edge.header.id || "";
              attributeMap.set(attrName, attrNodeId);
              addUniqueNode({
                id: attrNodeId,
                type: "customMKGNode",
                data: { header: edge.header },
                position: { x: 0, y: 0 },
              });
            }
            // Create edge between the metric and the attribute node
            metricEdgesData.push({
              id: `${metricId}-${attrNodeId}`,
              source: metricId,
              target: attrNodeId,
              animated: false,
              type: "smoothstep",
              style: { stroke: "#e2e8f0", strokeWidth: 2 },
              markerEnd: undefined,
            });

            break;
          }
          default:
            break;
        }
      });
    });

    if (metricNodesData.length === 0) return;

    // Force-directed layout (subgraph: scale to panel, slightly tighter links)
    const links = metricEdgesData.map(e => ({
      source: e.source,
      target: e.target,
      strength: e.data?.weight || 1,
    }));
    const minDim = Math.min(containerSize.width, containerSize.height);
    const ringR = isSubgraph
      ? Math.max(minDim * 0.22, 120)
      : 500;
    const linkDist = isSubgraph ? 130 : 160;
    const chargeStr = isSubgraph ? -280 : -400;
    const collideR = isSubgraph ? 95 : 110;
    const centerStr = isSubgraph ? 0.14 : 0.08;
    const ticks = isSubgraph ? 480 : 400;

    metricNodesData.forEach((node, i) => {
      const n = Math.max(metricNodesData.length, 1);
      node.x = Math.cos((2 * Math.PI * i) / n) * ringR;
      node.y = Math.sin((2 * Math.PI * i) / n) * ringR;
    });
    const sim = forceSimulation(metricNodesData)
      .force("link", forceLink(links).id((d: any) => d.id).distance(linkDist).strength(isSubgraph ? 0.75 : 0.6))
      .force("charge", forceManyBody().strength(chargeStr))
      .force("collide", forceCollide().radius(collideR))
      .force("center", forceCenter(containerSize.width / 2, containerSize.height / 2).strength(centerStr))
      .alphaDecay(isSubgraph ? 0.04 : 0.05)
      .stop();
    for (let i = 0; i < ticks; i++) sim.tick();
    metricNodesData.forEach(node => {
      node.position = { x: node.x, y: node.y };
    });

    setNodes(metricNodesData as any);
    setEdges(metricEdgesData as any);
    setOriginalEdges(metricEdgesData);
    setMetricNodesData(metricNodesData);
    setLayoutReady(true);
  }, [metricInfoMap, containerSize, isSubgraph]);

  // After nodes are placed, fit the whole graph into view
  useEffect(() => {
    if (!layoutReady) return;
    setLayoutReady(false);
    requestAnimationFrame(() => {
      setTimeout(() => {
        fitView(
          isSubgraph
            ? { padding: 0.2, maxZoom: 1.25, minZoom: 0.15 }
            : { padding: 0.12, maxZoom: 0.9 }
        );
      }, 50);
    });
  }, [layoutReady, fitView, isSubgraph]);

  // ─── Node click (full graph): first click selects + opens menu; same node again closes menu (keeps highlight) ─
  const onNodeClick = useCallback((event: React.MouseEvent, node: any) => {
    if (isSubgraph) {
      return;
    }

    const nodeData = node.data.header as Header;
    if (!nodeData?.id) return;

    const ctxId = selectedMetric?.id || selectedBenchmark?.id || selectedKgColumn?.id;
    const panelOpen = clickPos !== null && panelHeader !== null;

    if (ctxId === node.id) {
      if (panelOpen) {
        setClickPos(null);
        setPanelHeader(null);
        paneAwaitingSecondClickToClearRef.current = true;
        return;
      }
      setPanelHeader(nodeData);
      setClickPos({ x: event.clientX, y: event.clientY });
      paneAwaitingSecondClickToClearRef.current = false;
      return;
    }

    paneAwaitingSecondClickToClearRef.current = false;

    if (nodeData.type === MetadataTypeEnum.METRIC) {
      setSelectedMetric(nodeData);
      setSelectedBenchmark(null as any);
      setSelectedKgColumn(null as any);
    } else if (nodeData.type === MetadataTypeEnum.BENCHMARK) {
      setSelectedBenchmark(nodeData);
      fetchBenchmarkById(nodeData.id!);
      setSelectedMetric(null as any);
      setSelectedKgColumn(null as any);
    } else if (nodeData.type === MetadataTypeEnum.KG_COLUMN) {
      setSelectedKgColumn(nodeData);
      setSelectedMetric(null as any);
      setSelectedBenchmark(null as any);
    }

    setPanelHeader(nodeData);
    setClickPos({ x: event.clientX, y: event.clientY });
  },
    [
      isSubgraph,
      clickPos,
      panelHeader,
      selectedMetric?.id,
      selectedBenchmark?.id,
      selectedKgColumn?.id,
      fetchBenchmarkById,
      setSelectedMetric,
      setSelectedBenchmark,
      setSelectedKgColumn,
    ]
  );

  const onNodeMouseEnter = useCallback(
    (_: React.MouseEvent, node: any) => {
      if (isSubgraph) return;
      // If the panel is open, just return
      if (panelOpenRef.current) return;
      // Cancel any pending leave so moving between nodes doesn't briefly clear hover
      if (hoverLeaveTimerRef.current !== null) {
        clearTimeout(hoverLeaveTimerRef.current);
        hoverLeaveTimerRef.current = null;
      }
      setHoverNodeId(node.id);
    },
    [isSubgraph]
  );

  const onNodeMouseLeave = useCallback(() => {
    // Delay the clear slightly to prevent flickering when moving between nodes
    hoverLeaveTimerRef.current = setTimeout(() => {
      hoverLeaveTimerRef.current = null;
      setHoverNodeId(null);
    }, 60);
  }, []);

  // ─── Pane click: close panel first (keep subgraph); next pane click clears selection ─
  const onPaneClick = useCallback(() => {
    if (isSubgraph) {
      setClickPos(null);
      setPanelHeader(null);
      return;
    }

    setHoverNodeId(null);

    const panelOpen = clickPos !== null && panelHeader !== null;

    if (panelOpen) {
      setClickPos(null);
      setPanelHeader(null);
      paneAwaitingSecondClickToClearRef.current = true;
      return;
    }

    if (paneAwaitingSecondClickToClearRef.current) {
      paneAwaitingSecondClickToClearRef.current = false;
      setSelectedBenchmark(null as any);
      setSelectedKgColumn(null as any);
      setSelectedMetric(null as any);
      return;
    }

    paneAwaitingSecondClickToClearRef.current = false;
    setSelectedBenchmark(null as any);
    setSelectedKgColumn(null as any);
    setSelectedMetric(null as any);
  }, [
    isSubgraph,
    clickPos,
    panelHeader,
    setSelectedMetric,
    setSelectedBenchmark,
    setSelectedKgColumn,
  ]);

  const closePanel = useCallback(() => {
    if (isSubgraph) {
      setClickPos(null);
      setPanelHeader(null);
      return;
    }
    setHoverNodeId(null);
    setClickPos(null);
    setPanelHeader(null);
    paneAwaitingSecondClickToClearRef.current = true;
  }, [isSubgraph]);

  return (
    <div ref={containerRef} style={{ width: "100%", height: "100%", position: "relative" }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={onNodeClick}
        onNodeMouseEnter={onNodeMouseEnter}
        onNodeMouseLeave={onNodeMouseLeave}
        onPaneClick={onPaneClick}
        nodeTypes={nodeTypes}
        proOptions={{ hideAttribution: true }}
        minZoom={0.05}
        maxZoom={3}
        nodesDraggable={!isSubgraph}
        elementsSelectable={!isSubgraph}
      >
        <Background
          variant={BackgroundVariant.Dots}
          gap={20}
          size={1}
          color="#e2e8f0"
        />
        <Controls
          position="top-right"
          showInteractive={false}
          style={{ marginTop: 16, marginRight: 16, display: "flex", flexDirection: "row", gap: 4 }}
        />
        {showMinimap && (
          <MiniMap
            position="bottom-left"
            style={{ marginBottom: 52, marginLeft: 16, borderRadius: 8, border: "1px solid #e2e8f0" }}
            nodeColor={(n: any) => findNodeColor(n.data?.header) + "cc"}
            maskColor="rgba(241,245,249,0.75)"
            pannable
            zoomable
          />
        )}
      </ReactFlow>

      {/* ── Minimap toggle (full graph only) ───────────────────────────────── */}
      {!isSubgraph && (
        <Box position="absolute" bottom={4} left={4} zIndex={10}>
          <Tooltip
            label={showMinimap ? "Hide minimap" : "Show minimap"}
            placement="right"
            hasArrow
            bg="gray.700"
            color="white"
            fontSize="xs"
          >
            <IconButton
              aria-label={showMinimap ? "Hide minimap" : "Show minimap"}
              icon={<Icon as={FiMap} boxSize={4} />}
              size="sm"
              borderRadius="lg"
              bg={showMinimap ? "purple.600" : "white"}
              color={showMinimap ? "white" : "gray.500"}
              border="1px solid"
              borderColor={showMinimap ? "purple.600" : "gray.200"}
              boxShadow="sm"
              _hover={{ bg: showMinimap ? "purple.700" : "gray.50", color: showMinimap ? "white" : "gray.700" }}
              onClick={() => setShowMinimap(v => !v)}
            />
          </Tooltip>
        </Box>
      )}

      {/* ── Search bar (full graph only) ─────────────────────────────────────── */}
      {!isSubgraph && (
        <Box
          position="absolute"
          top={4}
          left="50%"
          transform="translateX(-50%)"
          zIndex={10}
          w="340px"
        >
          <InputGroup size="sm" bg="white" borderRadius="xl" boxShadow="md">
            <InputLeftElement pointerEvents="none" pl={2}>
              <Icon as={SearchIcon} color="gray.400" boxSize={3.5} />
            </InputLeftElement>
            <Input
              placeholder="Search nodes by name or type…"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              borderRadius="xl"
              border="1px solid"
              borderColor="gray.200"
              bg="white"
              fontSize="sm"
              _focus={{ borderColor: "purple.500", boxShadow: "0 0 0 2px var(--chakra-colors-purple-200)" }}
              _placeholder={{ color: "gray.400" }}
            />
          </InputGroup>
        </Box>
      )}

      {/* ── Node types legend (collapsible, filter by type) ─────────────────── */}
      <GraphLegend
        showLegend={showLegend}
        setShowLegend={setShowLegend}
        legendFilterKinds={legendFilterKinds}
        toggleLegendKind={toggleLegendKind}
      />

      {/* ── Floating edit panel ─────────────────────────────────────────────── */}
      {clickPos && panelHeader && (
        <MKGNodePopupContainer
          header={panelHeader}
          clickPos={clickPos}
          onClose={closePanel}
        />
      )}
    </div>
  );
};

// ─── Public wrapper ───────────────────────────────────────────────────────────
const KnowledgeGraphVisualizer: React.FC<KnowledgeGraphVisualizerProps> = ({
  metricInfoMap,
  variant = "full",
}) => (
  <ReactFlowProvider>
    <KnowledgeGraphVisualizerFlow metricInfoMap={metricInfoMap} variant={variant} />
  </ReactFlowProvider>
);

export default KnowledgeGraphVisualizer;